import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { formatValue, type BaseChartConfig, type Widget } from "./types";
import { AreaCard } from "./Area";

interface KpiConfig extends BaseChartConfig {
  /** Pre-rendered value (overrides anything from data.points). */
  value?: string | number;
  label?: string;
  delta?: number;
  up?: boolean;
  good?: boolean;
  /** When true and `data.points` has rows, the first numeric column from
   *  the first row drives `value`. */
  derive_from_data?: boolean;
}

export function KpiCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as KpiConfig;
  let value: string | number | undefined = cfg.value;
  if (value === undefined && widget.data?.points?.length) {
    const first = widget.data.points[0] as Record<string, unknown>;
    const numeric = Object.entries(first).find(
      ([k, v]) => k !== "label" && typeof v === "number",
    );
    if (numeric) value = numeric[1] as number;
  }
  const fmt = cfg.format ?? "number";
  const renderedValue =
    typeof value === "number" ? formatValue(value, fmt) : (value ?? "—");

  const tone =
    cfg.good === undefined
      ? "text.secondary"
      : cfg.good
        ? "success.main"
        : "error.main";

  // Sparkline beneath the KPI when data has multi-point series.
  const sparklineWidget: Widget | null =
    widget.data?.points && widget.data.points.length > 1
      ? { ...widget, kind: "sparkline" }
      : null;

  // Accent color: prefer config.color, else success/error/teal.
  const accent =
    cfg.color ??
    (cfg.good === undefined ? "#1f7a66" : cfg.good ? "#10b981" : "#ef4444");

  return (
    <Card variant="outlined" sx={{ position: "relative", overflow: "hidden" }}>
      {/* Top accent strip */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)`,
        }}
      />
      <CardContent sx={{ pt: 2, pb: "16px !important" }}>
        {widget.title && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 700,
              fontSize: 10.5,
              display: "block",
              mb: 1,
            }}
          >
            {widget.title}
          </Typography>
        )}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            color: "text.primary",
            fontSize: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
          }}
        >
          {renderedValue}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: "wrap" }}>
          {cfg.label && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {cfg.label}
            </Typography>
          )}
          {cfg.delta !== undefined && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                color: tone,
                bgcolor: `${tone === "success.main" ? "#10b98115" : tone === "error.main" ? "#ef444415" : "transparent"}`,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 700,
              }}
            >
              {cfg.up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12 }}>
                {cfg.up ? "+" : "−"}
                {Math.abs(cfg.delta)}%
              </Typography>
            </Box>
          )}
        </Stack>
        {sparklineWidget && (
          <Box sx={{ mt: 2, mx: -2, mb: -2.5 }}>
            <AreaCard widget={sparklineWidget} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
